import React, { createContext, useContext, useEffect, useState } from "react";
import {
  ERROR,
  LOADING,
  SUCCESS,
  treatment_program,
} from "../constants/common";
import { useAlert } from "./Alert";
import {
  checkedAssignUrl,
  deleteMember,
  getTeams,
  inviteTeamMemeber,
  registerMember,
  resendMemberInvitation,
  revokeMemberInvitation,
} from "../services/teams.services";
import { useAuth } from "./Auth";
import copy from "copy-to-clipboard";
import moment from "moment";
import { trackClickEvent } from "../mixpanel";

const TeamsContext = createContext({});

export const TeamsContextProvider = ({ children }) => {
  const { updateAuthState, handleSuccessfulRegistration } = useAuth();
  const { sendAlert } = useAlert();
  const [step, setStep] = useState(() => {
    const savedStep = localStorage.getItem("invitationStep");
    return savedStep ? parseInt(savedStep, 10) : 1;
  });

  const [teamsState, setTeamsState] = useState({
    inviteMemberStatus: "",
    checkUrlStatus: "",
    organizationData: {},
    registerMemberStatus: "",
    fetchTeamsStatus: "",
    userData: {},
    copyInvitationLinkStatus: {},
    removeMemberStatus: "",
    revokeInvitationStatus: "",
    resendInvitationStatus: "",
    teams: {
      accepted: [],
      pending: [],
    },
    invitationState: (() => {
      const savedState = localStorage.getItem("invitationState");
      return savedState
        ? JSON.parse(savedState)
        : {
            first_name: "",
            last_name: "",
            password: "",
            email: "",
            address1: "",
            city: "",
            state: 0,
            zip_code: "",
            lat: null,
            lng: null,
            degrees: [],
            licenses: [],
          };
    })(),
  });

  const sortArray = (arr) => {
    return arr.sort((a, b) => {
      // Sort by 'revoke' flag first: false should come before true
      if (a.revoke !== b.revoke) {
        return a.revoke - b.revoke;
      }
      // If 'revoke' flag is the same, sort by 'created_at' using moment
      return moment(b.created_at) - moment(a.created_at);
    });
  };

  const next = () => {
    setStep((prevState) => {
      const newStep = prevState + 1;
      localStorage.setItem("invitationStep", newStep.toString());
      return newStep;
    });
  };

  const prev = () => {
    setStep((prevState) => {
      const newStep = prevState > 1 ? prevState - 1 : prevState;
      localStorage.setItem("invitationStep", newStep.toString());
      return newStep;
    });
  };

  const handleState = (obj) => {
    setTeamsState((prevState) => ({
      ...prevState,
      ...obj,
    }));
  };

  const fetchTeams = async () => {
    handleState({
      fetchTeamsStatus: LOADING,
      teams: {
        accepted: [],
        pending: [],
      },
    });
    try {
      const response = await getTeams();
      const teams = response.data?.data;
      if (teams) {
        teams.pending = sortArray(teams.pending);
      }
      handleState({
        fetchTeamsStatus: SUCCESS,
        teams,
      });
    } catch (error) {
      handleState({ fetchTeamsStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const inviteMember = async (data) => {
    handleState({ inviteMemberStatus: LOADING });
    try {
      await inviteTeamMemeber(data);
      console.log('Invite data: ', data);
      handleState({
        inviteMemberStatus: SUCCESS,
      });
      sendAlert("Invited successfully", "success");
    } catch (error) {
      console.log('Invite data error: ', error);
      handleState({ inviteMemberStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const copyInvitationLink = async (data) => {
    handleState({ copyInvitationLinkStatus: LOADING });
    try {
      const response = await inviteTeamMemeber(data);
      handleState({
        copyInvitationLinkStatus: SUCCESS,
      });
      sendAlert("Link has been copied successfully", "success");
      copy(response?.data?.data);
      return response;
    } catch (error) {
      handleState({ copyInvitationLinkStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const checkUrl = async ({ route, expires, signature, signed_url }) => {
    handleState({ checkUrlStatus: LOADING });
    try {
      const response = await checkedAssignUrl({
        route,
        expires,
        signature,
        signed_url,
      });
      handleState({
        checkUrlStatus: SUCCESS,
        organizationData: response.data.data,
      });
      return response;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        throw new Error("Link expired");
      } else {
        handleState({ checkUrlStatus: ERROR });
        throw new Error("Unexpected error occurred");
      }
    }
  };

  const registerTeamMember = async (data) => {
    handleState({ registerMemberStatus: LOADING });
    try {
      const response = await registerMember(data);
      const userData = response.data.data;
      
      if(userData.can_login) {
        await handleSuccessfulRegistration(userData);
        localStorage.removeItem("invitationStep");
        localStorage.removeItem("invitationState");
      } else {
        const isMentalHealthProfessional = userData?.roles?.some(
          (role) => role.name === "mental_health_provider"
        );
        const accountType = isMentalHealthProfessional
        ? "user"
        : userData?.account_type;
      localStorage.setItem(
        "isMentalHealthProfessional",
        isMentalHealthProfessional
      );
      localStorage.setItem("account_type", accountType);
      localStorage.setItem("accessToken", userData.token);
      // TODO localStorage.setItem('userId', userData.user.id);
      localStorage.removeItem("invitationStep");
      localStorage.removeItem("invitationState");
      updateAuthState("account_type", accountType);
      updateAuthState("userData", userData);
      }
      
      trackClickEvent("Invitation - Registration Completed", {
        page: "Invitation Onboarding",
        button: "Sign-up",
        id: userData?.id,
      });
      
      handleState({
        registerMemberStatus: SUCCESS,
        userData,
      });
      return response;
    } catch (error) {
      handleState({ registerMemberStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      trackClickEvent("Invitation - Registration Failed", {
        page: "Invitation Onboarding",
        button: "Sign-up",
      });
      Promise.reject(error);
    }
  };

  const removeMember = async (data) => {
    handleState({ removeMemberStatus: LOADING });
    try {
      await deleteMember(data);
      handleState({
        removeMemberStatus: SUCCESS,
      });
      sendAlert("Removed successfully", "success");
    } catch (error) {
      handleState({ removeMemberStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const revokeInvitation = async (data) => {
    handleState({ revokeInvitationStatus: LOADING });
    try {
      await revokeMemberInvitation(data);
      handleState({
        revokeInvitationStatus: SUCCESS,
      });
      sendAlert("Revoked successfully", "success");
    } catch (error) {
      handleState({ revokeInvitationStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const resendInvitation = async (data) => {
    handleState({ resendInvitationStatus: LOADING });
    try {
      await resendMemberInvitation(data);
      handleState({
        resendInvitationStatus: SUCCESS,
      });
      sendAlert("Resent successfully", "success");
    } catch (error) {
      handleState({ resendInvitationStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const updateTeamsState = (field, value) => {
    handleState({ [field]: value });
  };

  const setInvitationState = (newState) => {
    handleState({ invitationState: newState });
  };

  const updateInvitationState = (updates) => {
    const newState = {
      ...teamsState.invitationState,
      ...updates,
    };
    handleState({ invitationState: newState });
    localStorage.setItem("invitationState", JSON.stringify(newState));
  };

  useEffect(() => {
    if (Boolean(localStorage?.getItem("accessToken"))) {
      setStep(4);
      localStorage.setItem("invitationStep", "4");
    }
  }, [localStorage]);

  const state = {
    ...teamsState,
    step,
    next,
    prev,
    fetchTeams,
    inviteMember,
    registerTeamMember,
    checkUrl,
    copyInvitationLink,
    removeMember,
    revokeInvitation,
    resendInvitation,
    updateTeamsState,
    setInvitationState,
    updateInvitationState,
  };

  return (
    <TeamsContext.Provider value={state}>{children}</TeamsContext.Provider>
  );
};

export const useTeams = () => useContext(TeamsContext);
